var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { extractTextFromReact } from '../../../utils/extractTextFromReact';
import styles from './TabsList.module.scss';
import { transitionDuration } from '../constants';
const TabList = (_a) => {
    var _b;
    var { onChange, selectedTabValue, label, classNames, fullWidth = false, theme = 'default', children } = _a, props = __rest(_a, ["onChange", "selectedTabValue", "label", "classNames", "fullWidth", "theme", "children"]);
    const tablistRef = useRef(null);
    const [childrenRefs, setChildrenRefs] = useState({});
    const [selectedValue, setSelectedValue] = useState(selectedTabValue);
    const [highlighterRect, setHighlighterRect] = useState();
    const [scrollAreaWidth, setScrollAreaWidth] = useState(1000);
    const [isAnimationEnabled, setAnimationEnabled] = useState(false);
    const animationTimeout = useRef();
    const childrenToRender = useMemo(() => React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, Object.assign(Object.assign({}, child.props), { ref: (ref) => setChildrenRefs((prevState) => (Object.assign(Object.assign({}, prevState), { [child.props.value]: ref }))), selectedValue, onClickCallback: handleClickButton, fullWidth,
                theme }));
        }
        return null;
    }), [selectedValue, onChange]);
    useEffect(() => () => {
        if (animationTimeout.current != null) {
            clearTimeout(animationTimeout.current);
        }
    }, []);
    useEffect(() => {
        if (selectedValue != null) {
            setHighlighterRectState();
        }
    }, [selectedValue, childrenRefs]);
    useEffect(() => {
        if (Object.keys(childrenRefs).length > 0) {
            setScrollAreaWidthState();
        }
    }, [childrenRefs]);
    useEffect(() => {
        setSelectedValue(selectedTabValue);
    }, [selectedTabValue]);
    const ariaLabel = (_b = props['aria-label']) !== null && _b !== void 0 ? _b : extractTextFromReact(label);
    const labelElement = label != null ? _jsx("p", Object.assign({ className: classnames(styles.label, classNames === null || classNames === void 0 ? void 0 : classNames.label) }, { children: label })) : null;
    const selectedItemHighlighterElement = selectedValue != null ? (_jsx("div", { className: classnames(styles.selectedItemHighlighter, styles[theme], { [styles.animated]: isAnimationEnabled }), style: Object.assign({ width: highlighterRect === null || highlighterRect === void 0 ? void 0 : highlighterRect.width, left: highlighterRect === null || highlighterRect === void 0 ? void 0 : highlighterRect.left }, { transitionDuration: isAnimationEnabled ? `${transitionDuration}ms` : '' }) })) : null;
    return (_jsxs("div", Object.assign({ className: classnames(styles.wrapper, classNames === null || classNames === void 0 ? void 0 : classNames.wrapper) }, { children: [labelElement, _jsx("div", Object.assign({ className: classnames(styles.tablistWrapper, classNames === null || classNames === void 0 ? void 0 : classNames.tablistWrapper) }, { children: _jsx("div", Object.assign({}, props, { ref: tablistRef, role: "tablist", className: classnames(styles.tablist, styles[theme], { [styles.fullWidth]: fullWidth }) }, (ariaLabel.length > 0 ? { 'aria-label': ariaLabel } : {}), { onScroll: handleScroll }, { children: _jsxs("div", Object.assign({ className: styles.tablistScrollContainer, style: { width: fullWidth ? '100%' : scrollAreaWidth } }, { children: [childrenToRender, selectedItemHighlighterElement] })) })) }))] })));
    function handleClickButton(value) {
        setSelectedValue(value);
        if (typeof onChange === 'function') {
            onChange(value);
        }
    }
    function setHighlighterRectState() {
        if (selectedValue == null || tablistRef.current == null || Object.keys(childrenRefs).length === 0) {
            return;
        }
        try {
            const tablistRect = tablistRef.current.getBoundingClientRect();
            const buttonRect = childrenRefs[String(selectedValue)].getBoundingClientRect();
            setHighlighterRect({ width: buttonRect.width, left: buttonRect.x - tablistRect.x + tablistRef.current.scrollLeft });
            if (!isAnimationEnabled) {
                animationTimeout.current = setTimeout(() => setAnimationEnabled(true), transitionDuration);
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    function setScrollAreaWidthState() {
        const childButtons = Object.values(childrenRefs);
        const updatedScrollAreaWidth = childButtons.reduce((accumulator, currentItem) => accumulator + currentItem.getBoundingClientRect().width, 0);
        setScrollAreaWidth(updatedScrollAreaWidth);
    }
    function handleScroll() {
        setAnimationEnabled(false);
        setHighlighterRectState();
    }
};
export default TabList;
